import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';

import AvatarInput from '../controlled/AvatarInput';
import {
  DataOuterContainerStyled,
  ExtraContainerStyled,
  InputContainerStyled,
  LabelStyled,
} from '../controlled/styled';
import { INPUT_TYPE, LABEL_TYPE } from '../types';
import CheckboxGroup from './CheckboxGroup';
import ErrorMessage from './ErrorMessage';
import {
  CheckboxInput,
  DatePickerInput,
  FileInput,
  NumberInput,
  RadioInput,
  RangePickerInput,
  SelectInput,
  SelectInputAsync,
  SwitchInput,
  TextAreaInput,
  TextInput,
  TimePickerInput,
} from './NewInputComponents';
import TextOnly from './TextOnly';
import { ICommonInputContainerProps, ICommonInputProps } from './types';

const CommonInput: React.FC<ICommonInputContainerProps> = ({
  type,
  label,
  labelType,
  className,
  name,
  extra,
  placeholder,
  disabled,
  inputOptions,
  inputSettings,
  value,
  defaultValue,
  onChange,
  style,
  onBlur,
  labelStyle,
  // isFormik,
  inputClassName,
  selectAsyncProps,
  checkboxGroupProps,
  onEnter,
  onKeyUp,
  addressSuggestionSubFields,
  addressSuggestion,
}) => {
  const formik = useFormikContext();

  const formikFieldProps: any = useMemo(() => {
    if (formik) {
      return [formik.getFieldProps(name), formik.getFieldMeta(name), formik.getFieldHelpers(name)];
    }
    return undefined;
  }, [formik]);

  const onInputChange = useCallback(
    (inputValue: any, ...rest: any) => {
      // element [2] is formik helper
      if (formikFieldProps && formikFieldProps[2] && formikFieldProps[2].setValue)
        try {
          formikFieldProps?.[2]?.setValue?.(inputValue);
        } catch (err) {
          console.log(err);
        }
      onChange?.(inputValue, ...rest);
    },
    [onChange],
  );

  const onInputBlur = useCallback((e: FocusEvent) => {
    formikFieldProps?.[0].onBlur(e);
  }, []);

  const commonInputProps: ICommonInputProps = useMemo(
    () => ({
      name,
      inputSettings,
      disabled,
      placeholder,
      inputOptions,
      value: value !== undefined ? value : formikFieldProps?.[0].value,
      defaultValue,
      type,
      onChange: onInputChange,
      onBlur: onInputBlur,
      inputClassName,
      checkboxGroupProps,
      onEnter,
      onKeyUp,
      addressSuggestionSubFields,
      addressSuggestion,
      error: formikFieldProps?.[1]?.error,
      setError: formikFieldProps?.[2]?.setError,
    }),
    [
      name,
      inputSettings,
      disabled,
      placeholder,
      inputOptions,
      value,
      defaultValue,
      type,
      onChange,
      onBlur,
      formikFieldProps?.[0].value,
      checkboxGroupProps,
      onEnter,
      onKeyUp,
      addressSuggestionSubFields,
      addressSuggestion,
    ],
  );

  const InputComponent = useMemo(() => {
    switch (type) {
      case INPUT_TYPE.EMAIL:
      case INPUT_TYPE.PASSWORD:
        return TextInput;

      case INPUT_TYPE.CHECKBOX:
        return CheckboxInput;

      // case INPUT_TYPE.CHECKBOX_GROUP:
      //   return CheckboxGroupInput

      case INPUT_TYPE.DATE_PICKER:
        return DatePickerInput;

      case INPUT_TYPE.TIME_PICKER:
        return TimePickerInput;

      case INPUT_TYPE.FILE:
        return FileInput;

      case INPUT_TYPE.NUMBER:
        return NumberInput;

      case INPUT_TYPE.RADIO:
        return RadioInput;

      case INPUT_TYPE.SELECT:
        return SelectInput;

      case INPUT_TYPE.TEXT_AREA:
        return TextAreaInput;

      case INPUT_TYPE.SWITCH:
        return SwitchInput;

      case INPUT_TYPE.RANGE_PICKER:
        return RangePickerInput;

      case INPUT_TYPE.AVATAR:
        return AvatarInput;

      case INPUT_TYPE.CHECKBOX_GROUP:
        return CheckboxGroup;

      case INPUT_TYPE.TEXT_ONLY:
        return TextOnly;

      default:
        return TextInput;
    }
  }, [type]);

  return (
    <div className={`${className} gx-flex-column`}>
      <DataOuterContainerStyled
        labelType={labelType}
        style={{
          // maxWidth: 350,
          marginBottom: 12,
          ...(style || {}),
        }}
        className={`input-outer-container name-${name} flex-1`}>
        {label && (
          <LabelStyled
            style={{
              ...labelStyle,
              fontWeight: type === INPUT_TYPE.CHECKBOX ? undefined : 500,
            }}
            $labelType={labelType}
            className={`label-container-${name}`}>
            <label htmlFor={name}>{label}</label>
          </LabelStyled>
        )}
        <InputContainerStyled
          style={{
            width: labelType === LABEL_TYPE.above || labelType === LABEL_TYPE.under || !labelType ? '100%' : undefined,
          }}
          className='input-field-container'>
          {!selectAsyncProps ? (
            <InputComponent {...commonInputProps} />
          ) : (
            type === INPUT_TYPE.SELECT && <SelectInputAsync {...commonInputProps} selectAsyncProps={selectAsyncProps} />
          )}
          {extra && <ExtraContainerStyled>{extra}</ExtraContainerStyled>}
        </InputContainerStyled>
      </DataOuterContainerStyled>
      <ErrorMessage name={name} />
    </div>
  );
};

export default React.memo(CommonInput);
